/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { SearchOption } from '@/types/search';

export const useAutocomplete = (
  fetchOptions: (query: string, context?: Record<string, any>) => Promise<SearchOption[]>,
  initialValue?: string,
  context?: Record<string, any>,
  dependsOn?: string[] // Track only specific fields to optimize re-fetching
) => {
  const [allOptions, setAllOptions] = useState<SearchOption[]>([]); // Full list from API
  const [filteredOptions, setFilteredOptions] = useState<SearchOption[]>([]); // Filtered list for display
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [selectedValue, setSelectedValue] = useState<SearchOption | null>(
    initialValue ? { key: initialValue, value: initialValue, label: initialValue } : null
  );

  // Serialize dependsOn array to avoid re-calculation when array reference changes
  const dependsOnKey = useMemo(() => {
    return dependsOn ? JSON.stringify(dependsOn) : null;
  }, [dependsOn]);

  // Track only specific fields from context (e.g., only 'department')
  // This prevents unnecessary re-fetching when other fields change
  const trackedContext = useMemo(() => {
    if (!context) {
      return JSON.stringify({});
    }

    // If no dependsOn specified, don't track any context changes
    // (options will only load on focus/input)
    if (!dependsOn || dependsOn.length === 0) {
      return null; // null means: don't auto-reload on context change
    }

    // Extract only the fields we care about
    const filtered: Record<string, any> = {};
    for (const key of dependsOn) {
      if (context[key] !== undefined && context[key] !== '') {
        filtered[key] = context[key];
      }
    }

    return JSON.stringify(filtered);
  }, [context, dependsOnKey]);

  // Track previous tracked context
  const previousTrackedContextRef = useRef<string | null | undefined>(undefined);

  // Stable reference to fetchOptions and context
  const fetchOptionsRef = useRef(fetchOptions);
  const contextRef = useRef(context);

  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
    contextRef.current = context;
  }, [fetchOptions, context]);

  // Local filter function - instant search without API call
  const filterOptions = useCallback((searchQuery: string, optionsList: SearchOption[]) => {
    if (!searchQuery || searchQuery.trim() === '') {
      return optionsList;
    }

    const query = searchQuery.toLowerCase().trim();
    return optionsList.filter((option) => {
      const label = option.label.toLowerCase();
      const value = String(option.value).toLowerCase();
      return label.includes(query) || value.includes(query);
    });
  }, []);

  const handleInputChange = useCallback(
    (newInputValue: string) => {
      setInputValue(newInputValue);
      // Filter locally from cached allOptions - instant, no API call
      const filtered = filterOptions(newInputValue, allOptions);
      setFilteredOptions(filtered);
    },
    [allOptions, filterOptions]
  );

  // Ref to track if options have been loaded
  const optionsLoadedRef = useRef(false);

  const handleFocus = useCallback(async () => {
    // Load full list when field is focused (only if not loaded yet)
    if (!optionsLoadedRef.current) {
      setLoading(true);
      try {
        const fetchedOptions = await fetchOptionsRef.current('', contextRef.current);
        setAllOptions(fetchedOptions);
        setFilteredOptions(fetchedOptions);
        optionsLoadedRef.current = true;
      } catch (error) {
        console.error('Error fetching autocomplete options:', error);
        setAllOptions([]);
        setFilteredOptions([]);
      } finally {
        setLoading(false);
      }
    }
    // If options already loaded, filtering happens automatically via handleInputChange
  }, []); // No dependencies - use refs for stable reference and current context

  const handleValueChange = useCallback((newValue: SearchOption | null) => {
    setSelectedValue(newValue);
    if (newValue) {
      setInputValue(newValue.label);
    }
  }, []);

  const clear = useCallback(() => {
    setInputValue('');
    setSelectedValue(null);
    setAllOptions([]);
    setFilteredOptions([]);
    optionsLoadedRef.current = false; // Reset loaded flag
  }, []);

  // Sync selectedValue with initialValue changes (for clearing)
  useEffect(() => {
    if (!initialValue || initialValue === '') {
      setSelectedValue(null);
      setInputValue('');
    }
  }, [initialValue]);

  // Reload options when tracked context changes (e.g., department changes)
  // Only tracks specific fields defined in dependsOn to avoid unnecessary API calls
  useEffect(() => {
    // Skip if trackedContext is null (no dependsOn specified)
    if (trackedContext === null) {
      previousTrackedContextRef.current = null;
      return;
    }

    // Only reload if tracked context actually changed (not initial render)
    if (
      previousTrackedContextRef.current !== undefined &&
      previousTrackedContextRef.current !== trackedContext
    ) {
      // DO NOT clear the current value - keep user's input for better UX
      // Only reload the options list

      // Clear cached options to force reload from API
      setAllOptions([]);
      setFilteredOptions([]);
      optionsLoadedRef.current = false; // Reset loaded flag

      // Debounce: wait a bit before fetching to avoid rapid successive calls
      // This is useful when multiple context fields change rapidly
      const timeoutId = setTimeout(() => {
        void (async () => {
          setLoading(true);
          try {
            // Use contextRef to get the latest context value
            const fetchedOptions = await fetchOptionsRef.current('', contextRef.current);
            setAllOptions(fetchedOptions);
            setFilteredOptions(fetchedOptions);
            optionsLoadedRef.current = true; // Mark as loaded
          } catch (error) {
            console.error('Error fetching autocomplete options on context change:', error);
            setAllOptions([]);
            setFilteredOptions([]);
            optionsLoadedRef.current = false;
          } finally {
            setLoading(false);
          }
        })();
      }, 100); // 100ms debounce

      // Update previous tracked context immediately
      previousTrackedContextRef.current = trackedContext;

      // Cleanup timeout on unmount or next change
      return () => clearTimeout(timeoutId);
    }

    // Update previous tracked context
    previousTrackedContextRef.current = trackedContext;
  }, [trackedContext, context, dependsOn]);

  return {
    options: filteredOptions, // Return filtered options for display
    loading,
    inputValue,
    selectedValue,
    handleInputChange,
    handleValueChange,
    handleFocus,
    clear,
  };
};
